import type { v2 } from '@sap/ux-specification-types';
import { ControlType } from '@sap/ux-specification-types';
import { syncRule, addPatternForBindingChangeOfEnumOP } from '../../../common/decoration';
import { getActionBreadcrumbParts, convertActionKeyToStableId } from '../../../common';
import type { ButtonType } from 'sap/m/library';
import type { URI } from 'sap/ui/core/library';
import { OP_STANDARD_FOOTER_ACTION_PREFIX } from '../../types';

/**
 * Create stable ID for Simple Action button.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs Array of breadcrumbs used for ID generation
 * @param {string[]} breadcrumbs Array of breadcrumb strings used for stable ID generation
 * @returns {string} stable ID of Action button
 */
const getActionButtonId = (baseId: string, _idBreadcrumbs: string[], breadcrumbs: string[]): string => {
    const { actionId } = getActionBreadcrumbParts(breadcrumbs);
    return `${baseId}action::${convertActionKeyToStableId(actionId).replace(':2f', '::')}`;
};

/**
 * Create stable ID for Standard Action button.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs Array of breadcrumbs used for intermediate ID generation
 * @param {string[]} breadcrumbs Array of breadcrumb strings used for stable ID generation
 * @returns {string} stable ID of Action button
 */
const getStandardActionButtonId = (baseId: string, _idBreadcrumbs: string[], breadcrumbs: string[]): string => {
    const { actionId } = getActionBreadcrumbParts(breadcrumbs);
    return `${baseId}template::${actionId}`;
};

/**
 * Create stable ID for Standard Action button on Object Page Footer.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs Array of intermediate breadcrumbs used for ID generation
 * @param {string[]} breadcrumbs Array of breadcrumb strings used for stable ID generation
 * @returns {string} stable ID of Action button
 */
const getStandardFooterActionOPButtonId = (baseId: string, _idBreadcrumbs: string[], breadcrumbs: string[]): string => {
    const { actionId } = getActionBreadcrumbParts(breadcrumbs);
    return `${baseId}${OP_STANDARD_FOOTER_ACTION_PREFIX}${actionId}`;
};

/**
 * Create stable ID for Intent Based Action button.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs Array of intermediate breadcrumbs used for ID generation
 * @param {string[]} breadcrumbs Array of breadcrumb strings used for stable ID generation
 * @returns {string} stable ID of Intent Based Action button
 */
export const buildIntentBasedActionId = (baseId: string, _idBreadcrumbs: string[], breadcrumbs: string[]): string => {
    const { semanticObjectId, actionId } = getActionBreadcrumbParts(breadcrumbs);
    return `${baseId}action::${semanticObjectId}::${convertActionKeyToStableId(actionId)}`;
};

/**
 * Create stable ID for (Intent Based) Footer Action button.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs Array of intermediate breadcrumbs used for ID generation
 * @param {string[]} breadcrumbs Array of breadcrumb strings used for stable ID generation
 * @returns {string} stable ID of Footer Action button
 */
const getFooterActionButtonId = (baseId: string, _idBreadcrumbs: string[], breadcrumbs: string[]): string => {
    const { actionId } = getActionBreadcrumbParts(breadcrumbs);
    return `${baseId}action::${convertActionKeyToStableId(actionId).replace(':2f', '::')}::Determining`;
};

const flexRuleForAction = {
    controlId: getActionButtonId,
    controlType: (): ControlType => ControlType.Button
};

const flexRuleForStandardAction = {
    controlId: getStandardActionButtonId,
    controlType: (): ControlType => ControlType.Button
};

const flexRuleForStandardFooterActionOP = {
    controlId: getStandardFooterActionOPButtonId,
    controlType: (): ControlType => ControlType.Button
};

const flexRuleForFooterAction = {
    controlId: getFooterActionButtonId,
    controlType: (): ControlType => ControlType.Button
};

export class Action implements v2.Action {
    @syncRule({
        flex: flexRuleForAction
    })
    tooltip?: string;

    @syncRule({
        flex: flexRuleForAction
    })
    icon?: URI;

    @syncRule({
        flex: flexRuleForAction
    })
    activeIcon?: URI;

    @syncRule({
        flex: flexRuleForAction
    })
    type?: ButtonType;
}

export class ToolBarAction implements v2.Action {
    @syncRule({
        flex: flexRuleForAction
    })
    tooltip?: string;

    @syncRule({
        flex: flexRuleForAction
    })
    icon?: URI;

    @syncRule({
        flex: flexRuleForAction
    })
    activeIcon?: URI;

    @syncRule({
        flex: flexRuleForAction
    })
    type?: ButtonType;
}

export class FooterAction implements v2.Action {
    @syncRule({
        flex: flexRuleForFooterAction
    })
    tooltip?: string;

    @syncRule({
        flex: flexRuleForFooterAction
    })
    icon?: URI;

    @syncRule({
        flex: flexRuleForFooterAction
    })
    activeIcon?: URI;

    @syncRule({
        flex: flexRuleForFooterAction
    })
    type?: ButtonType;
}

export class Share implements v2.Share {
    @syncRule({
        flex: flexRuleForStandardAction
    })
    visible?: boolean;
}

export class ShareOP implements v2.ShareOP {
    @syncRule({
        flex: flexRuleForStandardAction
    })
    visible?: boolean;
}

export class UpAndDownNavigationOP implements v2.UpAndDownNavigationOP {
    @syncRule({
        flex: flexRuleForStandardAction
    })
    visible?: boolean;
}

export class SaveAndEditOP implements v2.SaveAndEditOP {
    @syncRule({
        flex: flexRuleForStandardFooterActionOP,
        generate: addPatternForBindingChangeOfEnumOP
    })
    visible?: boolean;
}

export class HeaderActionStandard implements v2.HeaderActionStandard {
    @syncRule({
        flex: flexRuleForStandardAction
    })
    visible?: boolean;
}

export class ObjectPageHeaderActionStandard implements v2.ObjectPageHeaderActionStandard {
    @syncRule({
        flex: flexRuleForStandardAction
    })
    visible?: boolean;
}

export class ObjectPageFooterActionStandard implements v2.ObjectPageFooterActionStandard {
    @syncRule({
        flex: flexRuleForStandardFooterActionOP
    })
    visible?: boolean;
}
